
import moment from 'moment'
import axiosClient from '../../http'
import { mapActions, mapGetters } from 'vuex'
import store from '../../config/store'
import { replaceAccents } from '../../helpers/filters/filter'

export default {
  components: {
    'component-alert': () => import(/* webpackChunkName: "components" */ '@/common/components/Alert.vue'),
    'component-button': () => import(/* webpackChunkName: "components" */ '@/common/components/Button.vue'),
    'datepicker': () => import(/* webpackChunkName: "components" */ '@/common/components/Datepicker.vue'),
    'component-dropzone': () => import(/* webpackChunkName: "components" */ '@/common/components/dropzone'),
  },

  data: () => ({
    store,
    dropdown: false,
    formSubmited: false,
    transfer: {
      beneficiary: {
        id: null,
        international: '',
      },
      amount: '',
      type: 'direct',
      label: '',
      comment: '',
      documents: null,
      execution_date: null,
      period: '',
      start_date: null,
      end_date: null,
      currency: '',
      correspondent: '',
      international_documents: {},
    },
    period: ['monthly', 'quarterly', 'biannual', 'yearly'],
    selectedBeneficiary: '',
    accountUuid: store.data.user.account.uuid,
    disabled: {
      to: new Date()
    },
    disabledTo: {
      to: new Date()
    },
    loading: false,
    createTransferAction: 'create-transfer',
    transferCurrencies: [],
    step: 1,
    selectedCurrency: null,
    amountChange: null,
    exchangeRate: null,
    enterTimer: null,
    internationalDocumentsData: [],
    areInternationalDocumentsProvided: false,
  }),

  computed: {
    ...mapGetters([
      'accounts',
      'beneficiaryLists',
      'transferDetail'
    ]),

    account () {
      return store.data.user.account
    },

    isTransferDirect () {
      return this.transfer.type === 'direct'
    },

    isTransferDelayed () {
      return this.transfer.type === 'delayed'
    },

    isTransferPeriodic () {
      return this.transfer.type === 'periodic'
    },

    hasDocuments () {
      return this.transfer.documents && this.transfer.documents.length
    },

    transferDocumentsLabel () {
      if (this.transfer.documents.length === 1) {
        return this.transfer.documents[0].filename || this.transfer.documents[0].name
      }
      return this.transfer.documents.length + ' documents'
    },

    isExistingTransfer () {
      return !!this.$route.params.transferId
    },

    filteredBeneficiaryLists () {
      const filteredBeneficiaryLists = []

      if (!this.beneficiaryLists) {
        return filteredBeneficiaryLists
      }

      this.beneficiaryLists.forEach(beneficiaryList => {
        if (beneficiaryList.beneficiaries) {
          const beneficiaries = beneficiaryList.beneficiaries.filter(beneficiary =>
            (beneficiary.label !== null && replaceAccents(beneficiary.label).toLowerCase().includes(replaceAccents(this.selectedBeneficiary.toLowerCase()))) ||
            (beneficiary.short_tag !== null && beneficiary.short_tag.toLowerCase().includes(this.selectedBeneficiary.toLowerCase()))
          )

          if (beneficiaries.length !== 0) {
            const list = Object.assign({}, beneficiaryList)
            list.beneficiaries = beneficiaries
            filteredBeneficiaryLists.push(list)
          }
        }
      })

      return filteredBeneficiaryLists
    },

    transferData () {
      const transfer = { ...this.transfer }
      if (!this.isInternational) {
        delete transfer.currency
      }
      if (transfer.type === 'periodic') {
        transfer.start_date = moment(transfer.start_date).format('YYYY-MM-DD')
        transfer.end_date = moment(transfer.end_date).format('YYYY-MM-DD')
      } else if (transfer.type === 'delayed') {
        transfer.execution_date = moment(transfer.execution_date).format('YYYY-MM-DD')
      }
      return transfer
    },

    isInternational () {
      return !!this.transfer.beneficiary.international
    },

    areInternationalDocumentsRequired () {
      return this.isInternational && !!this.internationalDocumentsData.length
    },
  },

  watch: {
    'store.data.user.account.uuid': function () {
      this.transfer.beneficiary.id = null
      this.getBeneficiaryLists({
        expand: true
      })
    },

    'transfer.start_date': function () {
      this.disabledTo.to = new Date(this.transfer.start_date)
    },

    'isInternational' (value) {
      this.transfer.type = value ? 'import' : 'direct'
      this.getInternationalRequirements()
    },

    'transfer.amount' () {
      clearTimeout(this.enterTimer)
      this.enterTimer = setTimeout(() => {
        this.getInternationalRequirements()
        this.enterTimer = null
      }, 1000)
    },

    'transfer.currency' () {
      this.getInternationalRequirements()
    },

    'transfer.type' () {
      this.getInternationalRequirements()
    },
  },

  mounted () {
    this.loading = true
    this.getAccounts().then(() => {
      if (this.isExistingTransfer) {
        this.getTransferDetail(this.$route.params.transferId).then(() => {
          Object.assign(this.transfer, this.transferDetail)
          this.getAccounts().then(() => {
            this.loading = false
            this.selectBeneficiary(this.transferDetail.beneficiary)
          })
        }, () => {
          this.previousPage()
        })
      }
      this.getBeneficiaryLists({
        expand: true
      })
      this.loading = false
    })
    axiosClient.get('/currencies/international').then(response => {
      this.transferCurrencies = response.data
    })

    this.$bus.$on('local-file-added', this.onFileAttached)
    this.$bus.$on('beneficiary-save-success', this.onBeneficiarySaveSuccess)
    this.$bus.$on('password-checked', this.onPasswordChecked)
    this.$bus.$on('files-changed', this.onInternationalTransferFileAttached)
  },

  destroyed () {
    this.$bus.$off('local-file-added', this.onFileAttached)
    this.$bus.$off('beneficiary-save-success', this.onBeneficiarySaveSuccess)
    this.$bus.$off('password-checked', this.onPasswordChecked)
    this.$bus.$off('files-changed', this.onInternationalTransferFileAttached)
  },
  
  methods: {
    ...mapActions([
      'getBeneficiaryLists',
      'getTransferDetail',
      'createTransfer',
      'editTransfer',
      'switchAccount',
      'getAccounts'
    ]),

    changeAccount () {
      this.switchAccount(this.accounts.find(account => account.uuid === this.accountUuid))
    },

    addFiles () {
      this.showModal({
        childVue: 'join-document',
        wrapperClass: 'modal modal--md',
        dataModel: {
          isLocal: true,
          multipleFiles: true
        },
        autoQueue: false
      })
    },

    checkPassword () {
      this.showModal({
        childVue: 'check-password',
        wrapperClass: 'modal modal--xs',
        dataModel: {
          action: this.createTransferAction
        },
        blockLeaveBackground: true
      })
    },

    onFileAttached (files) {
      this.transfer.documents = files
    },

    removeFile () {
      this.transfer.documents = null
    },

    showCreateBeneficiary () {
      this.showSidePanel({
        childVue: 'new-beneficiary',
        dataModel: {},
        wrapperClass: 'sidepanel--xl',
        backgroundOverlay: true,
        closeButton: false,
      })
    },

    selectBeneficiary (beneficiary) {
      this.selectedBeneficiary = `${beneficiary.label} - ${beneficiary.iban}`
      this.transfer.beneficiary.id = beneficiary.id
      this.transfer.beneficiary.international = beneficiary.international
      this.errors.remove('beneficiary')
    },

    onFocusInputBeneficiary () {
      this.dropdown = true
      this.selectedBeneficiary = ''
      this.transfer.beneficiary.id = null
    },

    isDateTransferValid () {
      if (!['periodic', 'delayed'].includes(this.transfer.type)) {
        return true
      }
      if (this.transfer.type === 'delayed') {
        if (!this.transfer.execution_date) {
          this.$validator.errors.add({ field: 'execution-date' })
          return false
        }
      } else if (this.isTransferPeriodic) {
        if (!this.transfer.end_date) {
          this.$validator.errors.add({ field: 'enddate', msg: 'incorrect_date' })
          return false
        }
        if ((Date.parse(this.transfer.start_date) > Date.parse(this.transfer.end_date)) || !this.transfer.start_date) {
          this.$validator.errors.add({ field: 'date', msg: 'incorrect_date' })
          return false
        }
      }
      return true
    },

    onSubmit () {
      this.formSubmited = true
      this.errors.clear()
      if (!this.transfer.beneficiary.id) {
        this.$validator.errors.add({ field: 'beneficiary' })
      }
      const isDateTransferValid = this.isDateTransferValid()
      this.$validator.validateAll().then(result => {
        if (result && this.transfer.beneficiary.id && isDateTransferValid) {
          if (this.areInternationalDocumentsRequired && this.step === 1) {
            this.setStep(2)
            this.checkInternationalDocumentsProvided()
          } else if (!this.areInternationalDocumentsRequired || this.areInternationalDocumentsProvided) {
            this.checkPassword()
          }
        } else {
          this.$bus.$emit('scrollToTop')
        }
      })
    },

    onPasswordChecked (action) {
      if (action === this.createTransferAction) {
        if (this.isExistingTransfer) {
          this.editTransfer(this.transferData).then(() => {
            store.transfersTab = this.transferData.type
            window.localStorage.setItem(store.localStorageKey, JSON.stringify(store.data))
            this.showBanner('success', this.$i18n.t('banner.success.transfer.edit'))
            this.previousPage()
          })
        } else {
          this.createTransfer(this.transferData).then(() => {
            store.transfersTab = this.transferData.type
            window.localStorage.setItem(store.localStorageKey, JSON.stringify(store.data))
            this.showBanner('success', this.$i18n.t('banner.success.transfer.create'))
            this.previousPage()
          })
        }
      }
    },

    onBeneficiarySaveSuccess (beneficiary) {
      this.getBeneficiaryLists().then(() => {
        this.selectBeneficiary(beneficiary)
        this.hideSidePanel()
        this.getBeneficiaryLists({
          expand: true
        })
      })
    },

    previousPage () {
      this.$router.push('/transfers')
    },

    setStep (n) {
      this.$bus.$emit('scrollToTop')
      this.step = n
    },

    onInternationalTransferFileAttached (files, id) {
      if (this.internationalDocumentsData.find(document => document.key === id)) {
        this.transfer.international_documents[id] = files[0] || null
      }
      this.checkInternationalDocumentsProvided()
    },

    getInternationalRequirements () {
      if (this.isInternational && this.transfer.amount && !this.errors.first('amount') && this.transfer.currency && this.transfer.type) {
        this.amountChange = null
        this.exchangeRate = null
        axiosClient.get('/transfers/fees', {
          params: {
            amount: this.transfer.amount,
            currency: this.transfer.currency,
            type: this.transfer.type,
          }
        }).then(response => {
          this.amountChange = response.data.estimated_amount
          this.exchangeRate = response.data.exchange_rate
          this.internationalDocumentsData = response.data.documents
          this.transfer.international_documents = {}
          response.data.documents.forEach(document => {
            this.transfer.international_documents[document.key] = null
          })
        })
      }
    },

    checkInternationalDocumentsProvided () {
      for(const document of this.internationalDocumentsData) {
        if (document.required && !this.transfer.international_documents[document.key]) {
          this.areInternationalDocumentsProvided = false
          return
        }
      }
      this.areInternationalDocumentsProvided = true
    },
  }
};
